/**
 * Hook to load auth configuration from the runtime environment.
 *
 * On web, the optional `?authConnection=` query parameter overrides which
 * Auth0 connection to use (e.g. "Username-Password-Authentication" for E2E
 * testing with database credentials). This override takes precedence over
 * the email-domain-derived connection chosen on the Sign In screen — see
 * `AuthProvider.handleSignIn` and `@src/config/auth-connections`.
 *
 * On native this is always `undefined`; the connection is derived from the
 * email entered on the Sign In screen.
 */

import { Platform } from "react-native";
import { useEnvironment } from "@src/config/environment-store";
import { authorization } from "@src/config/runtime";

/** Auth configuration derived from runtime environment. */
export type AuthConfig = ReturnType<typeof authorization> & { authConnection?: string };

const authConnection =
  Platform.OS === "web" && typeof window !== "undefined" ?
    (new URLSearchParams(window.location.search).get("authConnection") ?? undefined)
  : undefined;

export function useAuthConfig(): AuthConfig {
  const env = useEnvironment();
  return { ...authorization(env), authConnection };
}
